// The account page (`/account`): who is signed in, the connected accounts,
// and Sign out. Reached from the quiet "Account" link in the Focus top bar.
//
// The connection rows (`GitHubConnection`, `FigmaConnection`,
// `GoogleConnection`, `GmailConnection`, `AnthropicKeyConnection`) each probe
// their own status route and report back with `onAvailability`. The page
// waits until all have reported before it shows the section, so nothing
// flashes in and out; the heading shows only above a row that renders, so it
// is absent when all are off in this environment. The Google row is the only
// place to connect or disconnect Google (meetings to tickets); the Gmail row
// links the Google account the Gmail add-on runs in.
//
// Sign out is the page's own job: an approved user has no other way out. It
// ends the Loom session only; the GitHub and Figma connections stay.

import { useEffect, useState } from "react";
import { z } from "zod";

import { fetchMe, invalidateMe, type AuthUser } from "../auth/useAuth";
import { FigmaConnection } from "../components/FigmaConnection";
import {
  GitHubConnection,
  type ConnectionAvailability,
} from "../components/GitHubConnection";
import { AnthropicKeyConnection } from "./AnthropicKeyConnection";
import { GmailConnection } from "./GmailConnection";
import { GoogleConnection } from "./GoogleConnection";
import { FocusFrame, Headline } from "../focus/ui";
import { useT } from "../i18n/index.js";
import { httpPost } from "../lib/http";
import { ThemeToggle } from "../theme/ThemeToggle";

const LogoutResponseSchema = z.object({ ok: z.boolean() });

export function AccountPage({
  onBack,
  onOpenProjects,
  onSignedOut,
}: {
  readonly onBack: () => void;
  /** After a GitHub connect: to the Focus picker, where the new projects are. */
  readonly onOpenProjects: () => void;
  /** After Sign out: a full navigation, so the app re-reads auth from scratch. */
  readonly onSignedOut: () => void;
}) {
  const t = useT();
  const [me, setMe] = useState<AuthUser | null>(null);
  const [github, setGithub] = useState<ConnectionAvailability | null>(null);
  const [figma, setFigma] = useState<ConnectionAvailability | null>(null);
  const [google, setGoogle] = useState<ConnectionAvailability | null>(null);
  const [gmail, setGmail] = useState<ConnectionAvailability | null>(null);
  const [anthropic, setAnthropic] = useState<ConnectionAvailability | null>(null);
  const [justConnected, setJustConnected] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((next) => {
        if (!cancelled) setMe(next);
      })
      .catch(() => {
        /* best-effort — the line stays away */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = [github, figma, google, gmail, anthropic];
  const settled = rows.every((row) => row !== null);
  // A failed row renders nothing, so the heading waits for a row that shows;
  // the failure line below speaks for a failed one.
  const anyRow = settled && rows.includes("shown");
  const anyFailed = rows.includes("failed");

  const signOut = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await httpPost("/auth/logout", {}, LogoutResponseSchema);
    } catch {
      // Best-effort — the cookie is cleared server-side regardless.
    }
    // Whoever was signed in is not any more: no guard may reuse the answer.
    invalidateMe();
    onSignedOut();
  };

  return (
    <FocusFrame stepKey="account" onBack={onBack} chromeAside={<ThemeToggle />}>
      <Headline>{t("account.headline")}</Headline>
      {me !== null && (
        <p className="account-identity">
          {me.name !== undefined && me.name !== ""
            ? t("account.signedInAs", { name: me.name, username: me.username })
            : t("account.signedInAsUsername", { username: me.username })}
        </p>
      )}
      {anyRow && (
        <h2 className="account-section-title">{t("account.connections")}</h2>
      )}
      {/* Mounted from the start so their status reads run; hidden until both
          have reported. */}
      <div className="account-rows" hidden={!settled}>
        <GitHubConnection
          onAvailability={setGithub}
          onConnected={() => setJustConnected(true)}
        />
        {justConnected && (
          <div className="account-to-projects">
            <button type="button" className="focus-btn" onClick={onOpenProjects}>
              {t("account.toProjects")}
            </button>
          </div>
        )}
        <FigmaConnection onAvailability={setFigma} />
        <GoogleConnection onAvailability={setGoogle} />
        <GmailConnection onAvailability={setGmail} />
        <AnthropicKeyConnection onAvailability={setAnthropic} />
      </div>
      {settled && anyFailed && (
        <p className="focus-error">{t("account.loadFailed")}</p>
      )}
      <div className="account-signout">
        <button
          type="button"
          className="account-act"
          disabled={signingOut}
          onClick={() => void signOut()}
        >
          {t("account.signOut")}
        </button>
      </div>
    </FocusFrame>
  );
}
